/* src/contexts/PWAInstallContext.js */
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';

// Create the context
const PWAInstallContext = createContext(null);

// Create the provider component
export const PWAInstallProvider = ({ children }) => {
    const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);

    // Effect to Listen for Android Install Prompt
    useEffect(() => {
        const handleBeforeInstallPrompt = (event) => {
            console.log('[PWA Install Context] beforeinstallprompt event fired.');
            event.preventDefault();
            setDeferredInstallPrompt(event);
            console.log('[PWA Install Context] Prompt event stored.');
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        console.log('[PWA Install Context] Added beforeinstallprompt listener.');
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            console.log('[PWA Install Context] Removed beforeinstallprompt listener.');
        };
    }, []);

    // Effect to listen for App Installed Event
    useEffect(() => {
        const handleAppInstalled = (event) => {
            console.log('[PWA Install Context] appinstalled event fired.', event);
            setDeferredInstallPrompt(null); // Clear the prompt once installed
        };
        window.addEventListener('appinstalled', handleAppInstalled);
        console.log('[PWA Install Context] Added appinstalled listener.');
        return () => {
            window.removeEventListener('appinstalled', handleAppInstalled);
            console.log('[PWA Install Context] Removed appinstalled listener.');
        };
    }, []);

    // Callback for Install Button Click
    const handleInstallClick = useCallback(async (callback) => { // Add callback optional arg
        if (!deferredInstallPrompt) {
            console.log('[PWA Install Context] handleInstallClick: No deferred prompt available.');
            return;
        }
        console.log('[PWA Install Context] handleInstallClick: Prompting user...');

        deferredInstallPrompt.prompt();
        try {
           const { outcome } = await deferredInstallPrompt.userChoice;
           console.log(`[PWA Install Context] User response to the install prompt: ${outcome}`);
           if (callback) callback(outcome); // Call callback if provided
        } catch (error) {
            console.error('[PWA Install Context] Error awaiting userChoice:', error);
            if (callback) callback('error');
        }
        // Clear prompt regardless of outcome
        setDeferredInstallPrompt(null);
        console.log('[PWA Install Context] Cleared deferred prompt.');

    }, [deferredInstallPrompt]);

    // Value provided by the context
    const value = {
        deferredInstallPrompt,
        handleInstallClick
    };

    return (
        <PWAInstallContext.Provider value={value}>
            {children}
        </PWAInstallContext.Provider>
    );
};

// Custom hook to use the context
export const usePWAInstall = () => {
    const context = useContext(PWAInstallContext);
    if (context === undefined) {
        throw new Error('usePWAInstall must be used within a PWAInstallProvider');
    }
    // Return null if context is not yet available (shouldn't happen if setup correctly)
    return context || { deferredInstallPrompt: null, handleInstallClick: () => console.warn("PWAInstallContext not ready") };
};